<template>
    <o-loading :active="loading">Loading</o-loading>
    <div class="is-flex is-justify-content-space-between" style="font-size: 13px;">
        <div style="min-width: 200px">
            <o-field label="Ticker" ref="ticker_ref">
                <o-autocomplete style="font-size: 12px;" v-model="tickerDisplay" clearable open-on-focus :data="tickers" @select="(option) => GetData(option)" :disabled="loading" :loading="isFetching" keep-first @typing="debouncedGetTickers" class="wide-dropdown" field="tn_ticker" async @focus="clearInputOnFocus">
                    <template #default="props">
                        {{ props.option.tn_ticker }} - {{ props.option.tn_name }}
                    </template>
                    <template #empty>
                        No results.
                    </template>
                </o-autocomplete>
            </o-field>
        </div>

        <div style="min-width: 100px">
            <o-field label="Start Date">
                <o-select v-model="startDate" @change="SetQuery()" expanded>
                    <option v-for="(d, i) in availablePeriodsForDates" :key="i" :value="d">{{ d.format("MMM YYYY") }}</option>
                </o-select>
            </o-field>
        </div>
        <div style="min-width: 100px">
            <o-field label="End Date">
                <o-select v-model="selectedPeriodId" @change="onPeriodChange()" expanded>
                    <option v-for="period in availablePeriods" :key="period.periodId" :value="period.periodId">
                        {{ period.periodName }}
                    </option>
                </o-select>
            </o-field>
        </div>
        <div>
            <o-field label="Year ranges">
                <o-button size="small" @click="SetDateRange(5)">5</o-button>
                <o-button size="small" @click="SetDateRange(15)">15</o-button>
                <o-button size="small" @click="SetDateRange(35)">35</o-button>
                <o-button size="small" @click="SetDateRange()">Max</o-button>
            </o-field>
        </div>
        <div>
            <o-field label="Avg Range">
                <o-select placeholder="12" @change="SetQuery()" @input="ChangedAvgRange($event.target.value)" expanded>
                    <option :value="12">12</option>
                    <option :value="18">18</option>
                    <option :value="24">24</option>
                    <option :value="30">30</option>
                    <option :value="36">36</option>
                </o-select>
            </o-field>
        </div>
        <div>
            <o-field label="Look Forward (mo)">
                <o-select v-model="forwardRange" @change="SetQuery()" expanded>
                    <option :value="6">6</option>
                    <option :value="12">12</option>
                    <option :value="18">18</option>
                    <option :value="24">24</option>
                    <option :value="36">36</option>
                    <option :value="48">48</option>
                    <option :value="60">60</option>
                </o-select>
            </o-field>
        </div>
        <div>
            <o-field label="Hide tooltip">
                <o-switch v-model="hideTooltip"></o-switch>
            </o-field>
        </div>
        <div>
            <o-field label="Group by Industry">
                <o-switch v-model="groupByIndustry" @input="toggleGrouping"></o-switch>
            </o-field>
        </div>
    </div>
    <div class="mt-4" style="font-size: 14px" v-if="ticker">
        <o-collapse :open="false">
            <template #trigger>
                Toggle tables
            </template>
            <growth-table :start-date="startDate" :end-date="endDate" :raw-data="data" :industryData="industryData" :indexData="indexData" :rawReleases="releases" :custom-options="customOptions"></growth-table>
        </o-collapse>
    </div>
    <div style="position: relative;" v-if="ticker && data.length > 0">
        <primary-chart :start-date="startDate" :end-date="endDate" :raw-data="data" :transactions="transactions" :custom-options="customOptions" :plugins="chartPlugins"></primary-chart>
        <tertiary-chart :start-date="startDate" :end-date="endDate" :raw-data="data" :deals="deals" :custom-options="customOptions" :plugins="chartPlugins"></tertiary-chart>
    </div>
    <div v-if="ticker && data.length > 0">
        <secondary-chart :start-date="startDate" :end-date="endDate" :raw-data="data" :custom-options="customOptions" :plugins="chartPlugins"></secondary-chart>
        <reversion-slope-chart :start-date="startDate" :end-date="endDate" :raw-data="data" :custom-options="customOptions" :plugins="chartPlugins"></reversion-slope-chart>
    </div>

    <div v-if="!ticker" class="mt-3">
        <ag-grid-vue :key="groupByIndustry ? 'grouped' : 'flat'" class="ag-theme-alpine" :loading="loading" :rowData="griddata" :defaultColDef="defaultColDef" domLayout="500" style="height: 55rem" :columnDefs="columnDefs" :gridOptions="gridOptions" @grid-ready="onGridReady"></ag-grid-vue>
    </div>

</template>
<script>
    import { ref, inject, computed, watch, onMounted, onBeforeMount } from "vue";
    import primaryChart from "@/components/research/primary-chart.vue";
    import secondaryChart from "@/components/research/secondary-chart.vue";
    import growthTable from "@/components/research/growth-table.vue";
    import tertiaryChart from "@/components/research/tertiary-chart.vue";
    import reversionSlopeChart from "@/components/research/reversion-slope-chart.vue";
    import { sharePlugin } from "@/components/research/plugins/share-plugin.js";
    import { verticalLine } from "@/components/research/plugins/vertical-line.js";
    import { transparentBackground } from "@/components/research/plugins/transparent-background.js";
    import { useRoute, useRouter } from "vue-router";
    import { AgGridVue } from "ag-grid-vue3";
    import "ag-grid-community/styles/ag-grid.css"; // Core grid CSS, always needed
    import "ag-grid-community/styles/ag-theme-alpine.css"; // Optional theme CSS
    import "ag-grid-enterprise";

    document.title = "Research";

    export default {
        components: {
            primaryChart,
            secondaryChart,
            growthTable,
            tertiaryChart,
            reversionSlopeChart,
            AgGridVue
        },
        setup(props, context) {
            const http = inject("$http");
            const moment = inject("$moment");
            const router = useRouter();
            const route = useRoute();

            const loading = ref(false);
            const ticker = ref("");
            const tickerDisplay = ref("");
            const tickers = ref([]);
            const startDate = ref(moment("01/01/1960", "MM/DD/YYYY").toDate());
            const endDate = ref(moment().toDate());
            const avgRange = ref();
            const forwardRange = ref(12);
            const hideTooltip = ref();
            const groupByIndustry = ref(true);
            const data = ref([]);
            const transactions = ref([]);
            const deals = ref([]);
            const industryData = ref([]);
            const indexData = ref([]);
            const releases = ref([]);
            const gridApi = ref({});
            const griddata = ref([]);
            const isFetching = ref(false);
            const precomputed = ref({});
            const selectedPeriodId = ref(null);
            const availablePeriods = ref([]);

            const ticker_ref = ref();

            const clearInputOnFocus = () => {
                tickerDisplay.value = '';
                tickers.value = [];
            };

            watch(tickerDisplay, (newValue) => {
                if (!newValue) {
                    ticker.value = "";
                    GetData(null);
                }
            });

            const percentFormatter = function (params) {
                if (params.value == null) return "0%";
                params.value *= 100;
                return formatNumber(params, 0) + "%";
            };

            const currencyFormatter = function (params) {
                if (params.value == null) return "";
                return "$" + formatNumber(params, 0);
            };

            const formatNumber = function (params, precision = 0) {
                if (params.value == null) return "";
                return parseFloat(params.value)
                    .toFixed(precision)
                    .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
            };

            const integerFormatter = function (params) {
                if (params.value == null) return "";
                return formatNumber(params, 0);
            }

            const singleDecimalFormatter = function (params) {
                if (params.value == null) return "";
                return formatNumber(params, 1);
            };

            const defaultColDef = {
                resizable: true,
                sortable: true,
                width: 110,
            };

            const median = (g) => {
                const values = g.values.sort(function (a, b) {
                    return a - b;
                });

                if (values.length == 0) return 0;
                else if (values.length % 2 == 0) {
                    const i = Math.floor(values.length / 2);
                    const a = values[i - 1];
                    const b = values[i];
                    return (a + b) / 2
                }
                else {
                    const i = Math.floor(values.length / 2);
                    return values[i];
                }
            }

            const tooltip = {
                var: "For each company we look at monthly returns compared to the median return of their industry. The trailing one year average of these variances is the VAR value.  Avg(Abs(historical_archive.total return-industry.total return...))",
                marketCap: "Market Cap",
                pe: "This is the trailing P/E Ratio.",
                pe_est: "This is the P/E Ratio based on the forward earnings estimate.",
                pSales: "This is the P/Sales Ratio based on the forward sales estimate and it is based on the enterprise value of a company, rather than the market cap, so debt will raise the ratio and cash will lower it.",
                threeYearRet: "Three Year Return",
                oneYearRet: "One Year Return",
                orgGrowth: "Future Sales Growth.  It is calculated by looking at sales estimates two years from now divided by sales estimates one year from now.  This far-reaching look eliminates noise connected to acquisitions and divestments, which would come from looking at sales estimates divided by trailing results.",
                rel: "This is the reversion calculated on a relative basis against the S&P 500.  It looks at a 2-year forward P/E ratio for a company and the S&P and compares that to the five-year trailing relationship between the two.  I think it is best applied to large and significant companies or entire industries that are themselves good proxies for the S&P.",
                value: "This looks at three values; a company's reversion based on its trailing P/E against 15 years of history, a company's reversion based on its forward P/E against 5 years of history, and a company's reversion based on its forward P/Sales against 5 years of history.  The 'Value' reversion is the median value of these three inputs.  I think it is best applied to all companies and industries that are not experiencing rapid change, either decelerating or accelerating growth beyond historic norms.",
                stdev: "",
                growth: "We calculate a version of a PEG ratio, but in this case we are looking at Enterprise Value/Estimated Gross Profits or Operating Profits/Organic Sales Growth.  The current ratio is compared to 5 years of history.  I think it is best applied to companies and industries experiencing rapid change.  For example, a slowing tech company with falling valuations will be better handled by this growth reversion than either Relative or Value reversions.  The math changes significantly though with small changes to forward sales growth estimate, so this reversion figure should be used with caution.",
                momentum: "This ranks all companies in the system based on three month stock returns and three month changes to earnings estimates.  Companies and industries with a high momentum score (near 100%) will have shown the best returns and the largest improvement to earnings estimates.",
                score: "",
            };

            const columnDefs = computed(() => [
                {
                    headerName: "Industry",
                    field: "Industry",
                    pinned: "left",
                    rowGroup: groupByIndustry.value,
                    hide: groupByIndustry.value,
                    width: 300
                },
                {
                    headerName: "Company",
                    field: "CompanyName",
                    hide: groupByIndustry.value,
                    width: 200
                },
                {
                    headerName: "Ticker",
                    field: "Link",
                    cellRenderer: params => {
                        
                        if (!params.data || !params.data.PsId) return '';

                        let url = `/research?ticker=${params.data.PsId}`;

                        // Include current endDate if it exists and is valid
                        if (endDate.value && moment(endDate.value).isValid()) {
                            const endDateStr = moment(endDate.value).format("MM-DD-YYYY");
                            url += `&endDate=${endDateStr}`;
                        }

                        return `<a target="_blank" href="${url}">${params.value}</a>`;
                    },
                    headerTooltip: "Ticker (click to view)"
                },
                //{ headerName: "Var", field: "Var", valueFormatter: percentFormatter, type: "rightAligned", aggFunc: median, headerTooltip: tooltip.var },
                { headerName: "MarketCap", field: "MarketCap", width: 130, valueFormatter: currencyFormatter, type: "rightAligned", aggFunc: 'sum', headerTooltip: tooltip.marketCap },
                { headerName: "PE", field: "PE", valueFormatter: singleDecimalFormatter, type: "rightAligned", aggFunc: median, headerTooltip: tooltip.pe },
                { headerName: "PE_est", field: "PE_est", valueFormatter: singleDecimalFormatter, type: "rightAligned", aggFunc: median, headerTooltip: tooltip.pe_est },
                { headerName: "PSales", field: "PSales", valueFormatter: singleDecimalFormatter, type: "rightAligned", aggFunc: median, headerTooltip: tooltip.pSales },
                //{ headerName: "ThreeYearRet", field: "ThreeYearRet", valueFormatter: percentFormatter, type: "rightAligned", aggFunc: median, headerTooltip: tooltip.threeYearRet },
                //{ headerName: "OneYearRet", field: "OneYearRet", valueFormatter: percentFormatter, type: "rightAligned", aggFunc: median, headerTooltip: tooltip.oneYearRet },
                { headerName: "OrgGrowth", field: "OrgGrowth", width: 125, valueFormatter: percentFormatter, type: "rightAligned", aggFunc: median, headerTooltip: tooltip.orgGrowth },
                //{ headerName: "Rel", field: "Rel", valueFormatter: percentFormatter, type: "rightAligned", aggFunc: median, headerTooltip: tooltip.rel },
                { headerName: "Reversion", field: "Reversion", width: 125, valueFormatter: percentFormatter, type: "rightAligned", aggFunc: median, headerTooltip: tooltip.value },
                { headerName: "Z-Score", field: "ZScore", width: 110, valueFormatter: singleDecimalFormatter, type: "rightAligned", aggFunc: median },
                //{ headerName: "Growth", field: "Growth", valueFormatter: percentFormatter, type: "rightAligned", aggFunc: median, headerTooltip: tooltip.growth },
                //{ headerName: "Score", field: "Score", valueFormatter: singleDecimalFormatter, type: "rightAligned", aggFunc: median, headerTooltip: tooltip.score },
                { headerName: "Low P/E", field: "LowPE", width: 110, valueFormatter: singleDecimalFormatter, type: "rightAligned", aggFunc: median },
                { headerName: "High P/E", field: "HighPE", width: 110, valueFormatter: singleDecimalFormatter, type: "rightAligned", aggFunc: median },
                { headerName: "PE Score", field: "PE_Score", valueFormatter: integerFormatter, type: "rightAligned", aggFunc: median },
                { headerName: "Growth", field: "Growth_Score", valueFormatter: integerFormatter, type: "rightAligned", aggFunc: median },
                { headerName: "Group", field: "Group_Score", valueFormatter: integerFormatter, type: "rightAligned", aggFunc: median },
                { headerName: "Total", field: "Total_Score", valueFormatter: integerFormatter, type: "rightAligned", aggFunc: median },

                //{
                //    headerName: "Links",
                //    field: "Links",
                //    cellRenderer: params => {
                //        return params?.value?.map(ticker => {
                //            let href = `https://datastudio.google.com/u/0/reporting/98cad188-6983-4346-8e11-8751d89a0a3e?params=%7B%22ds0.psc_ticker%22%3A%22${ticker}%22%7D`;
                //            return `<a href="${href}" target="_blank">${ticker}</a>`
                //        }).join(", ");
                //    }
                //}
            ]);
            const gridOptions = ref({
                enableColResize: true,
                enableSorting: true,
                suppressAutoSize: true,
                groupDefaultExpanded: 0,
                suppressAggFuncInHeader: true,

                autoGroupColumnDef: {
                    headerName: "Industry",
                    field: "Industry",
                    minWidth: 300,
                    pinned: "left",
                    sort: 'asc',
                    headerTooltip: "Industry Group",
                    cellRenderer: 'agGroupCellRenderer',
                    cellRendererParams: {
                        suppressCount: true,
                        innerRenderer: (params) => {
                            if (params.node.group) {
                                return `${params.value} (${params.node.allChildrenCount})`;
                            } else {
                                return params.data.CompanyName || '';
                            }
                        }
                    }
                },
                tooltipShowDelay: 200,
                groupRowAggNodes: (nodes) => {
                    if (!nodes || nodes.length === 0) {
                        return {};
                    }
                    const industry = nodes[0]?.data?.Industry?.trim()?.toLowerCase() ?? '';
                    
                    const pre = precomputed.value[industry] || {};
                    

                    // For Industry rows, use index data for LowPE and HighPE if available
                    let lowPE = pre.LowPE;
                    let highPE = pre.HighPE;

                    if (indexData.value && indexData.value.length > 0) {
                        const latestIndex = indexData.value[indexData.value.length - 1];
                        if (latestIndex && latestIndex.peLow !== undefined && latestIndex.peHigh !== undefined) {
                            lowPE = latestIndex.peLow;
                            highPE = latestIndex.peHigh;
                        }
                    }

                    return {
                        MarketCap: pre.MarketCap,
                        PE: pre.PE,
                        PE_est: pre.PE_est,
                        PSales: pre.PSales,
                        OrgGrowth: pre.OrgGrowth,
                        Reversion: pre.Reversion,
                        ZScore: pre.ZScore,
                        // Confidence and Momentum removed from grid
                        LowPE: lowPE,
                        HighPE: highPE,
                        PE_Score: pre.PE_Score,
                        Growth_Score: pre.Growth_Score,
                        Group_Score: pre.Group_Score,
                        Total_Score: pre.Total_Score,
                    };
                }
            });

            const startYearSelection = ref();

            const periods = computed(() => {
                return Array.from(new Set(data.value.map(d => moment(d["c_date"]))));
            });

            const availablePeriodsForDates = computed(() => {
                // Keep the original periods logic for date ranges, but we could enhance this later
                return periods.value;
            });

            const dates = computed(() => {
                return Array.from(new Set(data.value.map(d => moment().diff(moment(d["c_date"], "MM/DD/YYYY"), 'years')))).map(d => { return { year: d } }).filter(x => x.year > 0).reverse();
            });

            const customOptions = computed(() => {
                return {
                    avgRange: avgRange.value,
                    hideTooltip: hideTooltip.value,
                    forwardRange: forwardRange.value
                }
            });

            const chartPlugins = computed(() => {
                return [sharePlugin, verticalLine];
            });
            const ApplyQueryString = () => {
                if (route.query.ticker) ticker.value = route.query.ticker.toString() || ticker.value;
                if (route.query.startDate) startDate.value = route.query.startDate ? moment(route.query.startDate, 'MM-DD-YYYY') : startDate.value;
                if (route.query.endDate) {
                    const parsedEndDate = route.query.endDate ? moment(route.query.endDate, 'MM-DD-YYYY') : null;
                    if (parsedEndDate && parsedEndDate.isValid()) {
                        endDate.value = parsedEndDate;
                    }
                }
                if (route.query.avgRange) avgRange.value = route.query.avgRange.toString() || avgRange.value;
                if (route.query.forwardRange) forwardRange.value = Number(route.query.forwardRange) || forwardRange.value;
            }
            const SetQuery = (clear = false) => {
                if (clear)
                    router.replace({ path: route.path });
                else
                    router.replace({ path: route.path, query: newQueryObj.value });
            }

            //const queryString = computed(() => {
            //    const currentRoute = useRoute();
            //    const qs = Object.keys(currentRoute.query).map(key => `${key}=${currentRoute.query[key]}`).join("&");
            //    return qs.length == 0 ? "" : `?${qs}`;
            //});
            const newQueryObj = computed(() => {
                const queryObj = {
                    ticker: ticker.value,
                    avgRange: avgRange.value,
                    forwardRange: forwardRange.value
                };

                if (startDate.value) {
                    queryObj.startDate = moment(startDate.value).format("MM-DD-YYYY");
                }

                if (endDate.value && moment(endDate.value).isValid()) {
                    queryObj.endDate = moment(endDate.value).format("MM-DD-YYYY");
                }

                return queryObj;
            })

            const debounce = function (func, delay) {
                let timeoutId;
                return async function (...args) {
                    clearTimeout(timeoutId);

                    return new Promise((resolve) => {
                        timeoutId = setTimeout(async () => {
                            await func.apply(this, args);
                            resolve();
                        }, delay);
                    });
                };
            };

            const ChangedAvgRange = (val) => {
                debounce(() => {
                    avgRange.value = val;
                    SetQuery();
                }, 500)();
            }

            const SetDateRange = (d) => {
                if (d > 0) {
                    var flag = false;
                    var dte = moment().subtract(d, 'years').toDate();
                    periods.value.forEach(p => {
                        if (!flag && moment(p) > dte) {
                            startDate.value = p;
                            endDate.value = periods.value[periods.value.length - 1];
                            flag = true;
                        }
                    });
                    SetQuery();
                }
                else {
                    startDate.value = periods.value[0];
                    endDate.value = periods.value[periods.value.length - 1];
                }

                startYearSelection.value = d;
            }

            const GetData = async (selectedOption) => {
                SetQuery(true);

                if (!selectedOption) {
                    // Clear data if selection is cleared
                    data.value = [];
                    transactions.value = [];
                    deals.value = [];
                    industryData.value = [];
                    indexData.value = [];
                    releases.value = [];
                    if (griddata.value.length === 0) {
                        await LoadGrid();
                    }
                    return;
                }

                let psid;

                if (typeof selectedOption === 'object' && selectedOption !== null) {
                    psid = selectedOption.tn_psid;
                    ticker.value = psid;
                    tickerDisplay.value = `${selectedOption.tn_ticker} - ${selectedOption.tn_name}`;
                } else {
                    psid = selectedOption;
                    ticker.value = psid;
                    tickerDisplay.value = psid; // Temporary display until data load returns ticker/name
                }

                if (!psid) {
                    return;
                }

                loading.value = true;

                try {
                    const response = await http.get("/api/scoring/data?psid=" + encodeURIComponent(psid));
                    data.value = response.data.data;
                    transactions.value = response.data.transactions;
                    deals.value = response.data.deals;
                    industryData.value = response.data.industryData;
                    indexData.value = response.data.indexData;
                    releases.value = response.data.releases;
                    if (typeof selectedOption !== "object") {
                        const first = response?.data?.data?.[0];
                        if (first?.c_ticker && first?.c_name) {
                            tickerDisplay.value = `${first.c_ticker} - ${first.c_name}`;
                        }
                    }

                    SetDateRange();
                    ApplyQueryString();
                    SetQuery();
                }
                catch (err) {
                    console.error(err);
                }
                finally {
                    loading.value = false;
                }
            }

            const GetTickers = async (query = "") => {
                if (!query || query.length < 1) {
                    tickers.value = [];
                    return;
                }
                isFetching.value = true;
                try {
                    const response = await http.get(`/api/scoring/tickers?search=${encodeURIComponent(query)}`);
                    tickers.value = response.data;
                } catch (err) {
                    console.error("Error fetching tickers:", err);
                    tickers.value = [];
                } finally {
                    isFetching.value = false;
                }
            }

            const debouncedGetTickers = debounce(GetTickers, 300);

            const onPeriodChange = async () => {
                if (ticker.value) {
                    const selectedPeriod = availablePeriods.value.find(p => p.periodId === selectedPeriodId.value);
                    if (selectedPeriod && selectedPeriod.periodDate) {
                        endDate.value = moment(selectedPeriod.periodDate);
                    }
                    SetQuery();
                    return;
                }

                await LoadGrid();
                // Also update the endDate to match the selected period
                const selectedPeriod = availablePeriods.value.find(p => p.periodId === selectedPeriodId.value);
                if (selectedPeriod && selectedPeriod.periodDate) {
                    endDate.value = moment(selectedPeriod.periodDate);
                }
            }

            const buildResearchQuery = (includeDetails = true) => {
                const params = new URLSearchParams();
                if (selectedPeriodId.value) {
                    params.set("periodId", String(selectedPeriodId.value));
                }
                if (!includeDetails) {
                    params.set("includeDetails", "false");
                }

                const queryString = params.toString();
                return queryString ? `?${queryString}` : "";
            }

            const LoadGrid = async () => {
                try {
                    loading.value = true;
                    const periodParam = buildResearchQuery(true);

                    const response = await http.get(`/api/gdrive/research${periodParam}`);
                    griddata.value = response.data.details;
                    availablePeriods.value = response.data.periods || [];
                    precomputed.value = response.data.groups ? response.data.groups.reduce((acc, g) => {
                            acc[g.Industry.trim().toLowerCase()] = g;
                            return acc;
                        }, {}) : {};

                    // Set default to last period if not already set
                    if (!selectedPeriodId.value && response.data.selectedPeriod) {
                        selectedPeriodId.value = response.data.selectedPeriod;
                        // Also set the endDate to match
                        const selectedPeriod = availablePeriods.value.find(p => p.periodId === selectedPeriodId.value);
                        if (selectedPeriod && selectedPeriod.periodDate) {
                            endDate.value = moment(selectedPeriod.periodDate);
                        }
                    }

                    loading.value = false;
                }
                catch (err) {
                    console.error(err);
                    loading.value = false;
                }
            }

            const LoadPeriodsOnly = async () => {
                try {
                    loading.value = true;
                    const periodParam = buildResearchQuery(false);

                    const response = await http.get(`/api/gdrive/research${periodParam}`);

                    availablePeriods.value = response.data.periods || [];
                    if (!selectedPeriodId.value && response.data.selectedPeriod) {
                        selectedPeriodId.value = response.data.selectedPeriod;
                        const selectedPeriod = availablePeriods.value.find(p => p.periodId === selectedPeriodId.value);
                        if (selectedPeriod && selectedPeriod.periodDate) {
                            endDate.value = moment(selectedPeriod.periodDate);
                        }
                    }

                    loading.value = false;
                }
                catch (err) {
                    console.error(err);
                    loading.value = false;
                }
            }

            const toggleGrouping = () => {
                // The key attribute on ag-grid-vue will handle the re-rendering automatically
                // when groupByIndustry changes, so we don't need manual grid manipulation
            }

            const onGridReady = (params) => {
                gridApi.value = params.api;
                // Remove auto-sizing to allow user-controlled column widths
                // params.columnApi.autoSizeAllColumns();
            }

            watch(() => newQueryObj.value, (n, o) => {
                //console.log(n, o);
                //SetQuery();
            });



            onBeforeMount(() => {
                //griddata.value = [{
                //    Industry: "Test1",
                //    Count: 2,
                //    Links: ["https://facebook.com", "https://google.com"]
                //}]; //TestData
                ApplyQueryString();
            });

            onMounted(async () => {
                if (ticker.value) {
                    await LoadPeriodsOnly();
                }
                else {
                    // Landing mode needs full grid data
                    await LoadGrid();
                }

                // Then handle ticker data if present
                if (ticker.value)
                    await GetData(ticker.value);
            });


            return {
                loading,
                ticker,
                tickerDisplay,
                tickers,
                startDate,
                endDate,
                avgRange,
                forwardRange,
                hideTooltip,
                groupByIndustry,
                toggleGrouping,
                dates,
                periods,
                startYearSelection,
                data,
                customOptions,
                chartPlugins,
                defaultColDef,
                ChangedAvgRange,
                SetDateRange,
                GetData,
                LoadGrid,
                onPeriodChange,
                transparentBackground,
                SetQuery,
                transactions,
                deals,
                industryData,
                indexData,
                releases,
                griddata,
                columnDefs,
                onGridReady,
                gridOptions,
                ticker_ref,
                isFetching,
                debouncedGetTickers,
                clearInputOnFocus,
                selectedPeriodId,
                availablePeriods,
                availablePeriodsForDates
            }
        }
    }
</script>

<style scoped>
    .wide-dropdown .o-drop__menu {
        min-width: 300px;
        /* Adjust this value as needed */
        /* Or use width: auto; max-width: none; if you want it to auto-size based on content */
    }
</style>
